import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Layout, Card, Button, DatePicker, Input, InputNumber, Switch, Table, Tag, Tabs, Select, Space, Typography, message, Popconfirm, Modal, Alert, Spin, Avatar, Progress, Empty } from 'antd';
import { ArrowLeftOutlined, PlusOutlined, ReloadOutlined, CalendarOutlined, ClockCircleOutlined, DeleteOutlined, EditOutlined, ThunderboltOutlined, UserOutlined, PhoneOutlined, SearchOutlined, TeamOutlined, CheckCircleOutlined, AppstoreOutlined, RightOutlined } from '@ant-design/icons';
import dayjs, { Dayjs } from 'dayjs';
import 'dayjs/locale/ar';
import 'dayjs/locale/en';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import API from '../config/api';
import DashboardHeader from './DashboardHeader';
import DashboardSidebar from './DashboardSidebar';
import { useClinicContext } from './ClinicContext';
import { useDoctorContext } from './DoctorContext';
import { useLanguage } from './LanguageContext';
import { appointmentInstantToWallTime, appointmentWallTimeToIso, formatAppointmentDateTime, formatAppointmentInstant, formatAppointmentMinutes } from '../utils/appointmentTime';
import './AppointmentSlotsManagement.css';

const { Content } = Layout;
const { Title, Text } = Typography;
const BASE = '/api/slot-scheduling/doctors';
const TIME_VALUES = Array.from({ length: 288 }, (_, index) => index * 5);
const WEEKDAY_VALUES = [6, 0, 1, 2, 3, 4, 5];
const ACTIVE_APPOINTMENT_STATUSES = new Set(['reserved', 'confirmed', 'arrived']);

const bookingCopy = {
  ar: {
    overview: 'حجوزاتي القادمة', overviewHint: 'كل المواعيد والمرضى في مكان واحد', today: 'حجوزات اليوم', upcomingPatients: 'مرضى قادمون', openSeats: 'أماكن متاحة', nextBooking: 'أقرب حجز', noNextBooking: 'لا يوجد حجز قادم', search: 'ابحث باسم المريض أو رقم الهاتف', allSlots: 'كل المواعيد', bookedOnly: 'المحجوز فقط', availableOnly: 'المتاح فقط', bookedPatients: 'المرضى المحجوزون', noPatients: 'لا توجد حجوزات في هذا الموعد', seats: 'أماكن', remaining: 'متبقي', full: 'مكتمل', closed: 'مغلق', available: 'متاح', archivedSlot: 'الموعد غير ظاهر في الجدول', archivedHelp: 'الحجز محفوظ، لكن الـslot الأصلي محذوف أو غير نشط.', openFile: 'فتح ملف المريض', call: 'اتصال', noDaySlots: 'لا توجد مواعيد مطابقة في هذا اليوم', slots: 'مواعيد', patients: 'مرضى', readOnly: 'عرض آمن — بدون أي تغيير على الحجوزات', chooseDay: 'اختر اليوم', manage: 'إدارة ونشر المواعيد', allBookings: 'جميع الحجوزات', activeBookings: 'الحجوزات الفعالة', cancelledBookings: 'الملغية والمنتهية',
  },
  en: {
    overview: 'Upcoming bookings', overviewHint: 'Every slot and patient in one clear view', today: "Today's bookings", upcomingPatients: 'Upcoming patients', openSeats: 'Open seats', nextBooking: 'Next booking', noNextBooking: 'No upcoming booking', search: 'Search patient name or phone', allSlots: 'All slots', bookedOnly: 'Booked only', availableOnly: 'Available only', bookedPatients: 'Booked patients', noPatients: 'No bookings in this slot', seats: 'seats', remaining: 'remaining', full: 'Full', closed: 'Closed', available: 'Available', archivedSlot: 'Slot is no longer on the timetable', archivedHelp: 'The booking is preserved, but its original slot was deleted or is inactive.', openFile: 'Open patient file', call: 'Call', noDaySlots: 'No matching slots on this day', slots: 'slots', patients: 'patients', readOnly: 'Safe view — no booking data is changed', chooseDay: 'Choose day', manage: 'Manage & publish slots', allBookings: 'All bookings', activeBookings: 'Active bookings', cancelledBookings: 'Cancelled & finished',
  },
} as const;

const copy = {
  ar: {
    branch: 'الفرع:', selectBranch: 'اختر الفرع', loadingBranch: 'جارٍ تحميل إعدادات الفرع...',
    title: 'الجدول والمواعيد', subtitle: 'نظام مستقل عن قائمة الانتظار الحالية', refresh: 'تحديث', workMode: 'نظام العمل:', queue: 'قائمة الانتظار', slotsMode: 'مواعيد محددة', modeHelp: 'عند نشر أي موعد، سيتم تفعيل نظام المواعيد المحددة تلقائياً لهذا الفرع ليظهر الحجز للمساعد.', autoModeActivated: 'تم تفعيل نظام المواعيد المحددة تلقائياً', autoModeError: 'تم إنشاء المواعيد، لكن تعذر تفعيل نظام المواعيد المحددة. اختر «مواعيد محددة» من نظام العمل ثم أعد المحاولة.', timezone: 'المنطقة الزمنية',
    manage: 'إدارة الجدول', bookings: 'الحجوزات', generator: 'مولّد المواعيد', startDate: 'تاريخ البداية', repeatDays: 'التكرار لمدة (أيام)', dayStart: 'بداية اليوم', dayEnd: 'نهاية اليوم (آخر موعد)', duration: 'مدة الموعد (دقيقة)', capacity: 'سعة كل موعد', workDays: 'أيام العمل', weekdays: ['الأحد','الإثنين','الثلاثاء','الأربعاء','الخميس','الجمعة','السبت'], sunThu: 'الأحد–الخميس', weekend: 'نهاية الأسبوع', all: 'الكل', appointment: 'موعد', appointmentsWord: 'موعداً', during: 'خلال', days: 'يوم', noGenerated: 'لا توجد مواعيد ضمن الاختيارات الحالية', first: 'الأول', last: 'الأخير', publish: 'نشر', addSingle: 'إضافة موعد واحد فقط', date: 'التاريخ', time: 'الوقت', add: 'إضافة',
    published: 'المواعيد المنشورة القادمة', total: 'الإجمالي', available: 'متاح', full: 'مكتمل', closed: 'مغلق', booked: 'محجوز', noPublished: 'لم يتم نشر مواعيد مستقبلية بعد.', details: 'تفاصيل وإدارة كل المواعيد', reserved: 'محجوز', enabled: 'مفعل', actions: 'الإجراءات', edit: 'تعديل الموعد', editBookedHint: 'تعديل السعة أو حالة الموعد؛ الوقت مقفل لوجود حجوزات', delete: 'حذف', deleteQuestion: 'حذف هذا الموعد؟', deleteKeep: 'حذف هذا الموعد؟ الحجوزات الحالية ستظل محفوظة.', deleteAll: 'حذف كل المواعيد القادمة', deleteAllTitle: 'حذف كل المواعيد المنشورة القادمة؟', deleteAllWarning: 'سيتم حذف كل المواعيد الظاهرة للطبيب والفرع المحددين. الحجوزات الحالية ستظل محفوظة ولن يتم إلغاؤها.', deleteAllType: 'للتأكيد، اكتب اسم الفرع كما هو موضح أدناه:', deleteAllSuccess: 'تم حذف كل المواعيد القادمة', deleteAllMismatch: 'اسم الفرع غير مطابق', patient: 'المريض', phone: 'الهاتف', status: 'الحالة', noBookings: 'لا توجد حجوزات مجدولة.', dateTime: 'التاريخ والوقت', save: 'حفظ التعديلات', cancel: 'إلغاء', currentReserved: 'محجوز حالياً', activeSlot: 'الموعد مفعل', lockedHelp: 'يمكنك تعديل السعة أو تفعيل الموعد، لكن لا يمكن تغيير التاريخ والوقت لوجود حجوزات حالية.', loadError: 'تعذر تحميل جدول المواعيد', modeSaved: 'تم حفظ نظام المواعيد. نظام الانتظار الحالي لم يتغير.', modeError: 'تعذر حفظ النظام', invalidEnd: 'آخر موعد يجب أن يكون بعد أو مساوياً لأول موعد', selectWorkday: 'اختر يوم عمل واحداً على الأقل', tooMany: 'الفترة كبيرة جداً. اختر فترة أقصر من 2000 موعد.', createError: 'تعذر إنشاء المواعيد', futureOnly: 'اختر موعداً في المستقبل', createOneError: 'تعذر إنشاء الموعد', updateError: 'تعذر تعديل الموعد', capacityError: 'لا يمكن أن تقل السعة عن عدد الحجوزات الحالي', endAfterStart: 'وقت النهاية يجب أن يكون بعد وقت البداية', updated: 'تم تحديث الموعد', deleted: 'تم حذف الموعد', deleteError: 'تعذر حذف الموعد', statuses: { reserved: 'محجوز', confirmed: 'مؤكد', arrived: 'حضر', completed: 'مكتمل', cancelled: 'ملغي', no_show: 'لم يحضر' },
  },
  en: {
    branch: 'Branch:', selectBranch: 'Select branch', loadingBranch: 'Loading branch settings...',
    title: 'Timetable & Appointments', subtitle: 'A scheduling system independent from the current waiting queue', refresh: 'Refresh', workMode: 'Work mode:', queue: 'Waiting queue', slotsMode: 'Scheduled appointments', modeHelp: 'Publishing any slot automatically activates scheduled appointments for this branch so assistants can book it.', autoModeActivated: 'Scheduled appointments were activated automatically', autoModeError: 'Slots were created, but scheduled appointments could not be activated. Select “Scheduled appointments” under Work mode, then retry.', timezone: 'Time zone', manage: 'Manage timetable', bookings: 'Bookings', generator: 'Slot generator', startDate: 'Start date', repeatDays: 'Repeat for (days)', dayStart: 'Day starts at', dayEnd: 'Day ends at (last slot)', duration: 'Appointment duration (minutes)', capacity: 'Capacity per slot', workDays: 'Working days', weekdays: ['Sunday','Monday','Tuesday','Wednesday','Thursday','Friday','Saturday'], sunThu: 'Sun–Thu', weekend: 'Weekend', all: 'All', appointment: 'appointment', appointmentsWord: 'appointments', during: 'during', days: 'days', noGenerated: 'No slots match the current choices', first: 'First', last: 'Last', publish: 'Publish', addSingle: 'Add one slot only', date: 'Date', time: 'Time', add: 'Add', published: 'Upcoming published slots', total: 'Total', available: 'available', full: 'Full', closed: 'Closed', booked: 'Booked', noPublished: 'No future slots have been published yet.', details: 'Details and management of all slots', reserved: 'Reserved', enabled: 'Enabled', actions: 'Actions', edit: 'Edit slot', editBookedHint: 'Change capacity or enabled status; time is locked because this slot has bookings', delete: 'Delete', deleteQuestion: 'Delete this slot?', deleteKeep: 'Delete this slot? Existing bookings will remain saved.', deleteAll: 'Delete all upcoming slots', deleteAllTitle: 'Delete all upcoming published slots?', deleteAllWarning: 'This deletes every displayed slot for the selected doctor and branch. Existing patient bookings remain saved and are not cancelled.', deleteAllType: 'To confirm, type the branch name exactly as shown below:', deleteAllSuccess: 'All upcoming slots were deleted', deleteAllMismatch: 'The branch name does not match', patient: 'Patient', phone: 'Phone', status: 'Status', noBookings: 'No scheduled bookings.', dateTime: 'Date and time', save: 'Save changes', cancel: 'Cancel', currentReserved: 'Currently reserved', activeSlot: 'Slot enabled', lockedHelp: 'You can change capacity or enabled status, but date and time are locked because this slot has bookings.', loadError: 'Could not load the timetable', modeSaved: 'Appointment mode saved. The existing queue system was not changed.', modeError: 'Could not save appointment mode', invalidEnd: 'The last slot must be at or after the first slot', selectWorkday: 'Select at least one working day', tooMany: 'The range is too large. Choose a range with fewer than 2,000 slots.', createError: 'Could not create slots', futureOnly: 'Choose a future time', createOneError: 'Could not create the slot', updateError: 'Could not update the slot', capacityError: 'Capacity cannot be lower than current bookings', endAfterStart: 'End time must be after start time', updated: 'Slot updated', deleted: 'Slot deleted', deleteError: 'Could not delete the slot', statuses: { reserved: 'Reserved', confirmed: 'Confirmed', arrived: 'Arrived', completed: 'Completed', cancelled: 'Cancelled', no_show: 'No show' },
  },
} as const;

type Slot = { _id: string; start_at: string; end_at: string; capacity: number; reserved_count: number; available: number; enabled: boolean; is_full: boolean };
type Appointment = { _id: string; slot_id?: string; patient_id?: string; patient_name: string; patient_phone: string; start_at: string; end_at?: string; status: string; notes?: string };
type BookingFilter = 'all' | 'booked' | 'available';
type BookingStatusFilter = 'active' | 'all' | 'inactive';
type ScheduleEntry = { key: string; slot?: Slot; start_at: string; end_at?: string; appointments: Appointment[]; archived: boolean };

const appointmentStatusColor = (status: string) => ({
  reserved: 'blue', confirmed: 'green', arrived: 'cyan', completed: 'default', cancelled: 'red', no_show: 'orange',
}[status] || 'default');

const patientInitials = (name: string) => String(name || '?').trim().split(/\s+/).slice(0, 2).map(part => part[0]).join('').toUpperCase();

export default function AppointmentSlotsManagement() {
  const { language, isRTL } = useLanguage();
  const text = copy[language];
  const locale = language === 'ar' ? 'ar' : 'en';
  const timeOptions = useMemo(() => TIME_VALUES.map(minutes => ({ value: minutes, label: formatAppointmentMinutes(minutes, language) })), [language]);
  const [collapsed, setCollapsed] = useState(false);
  const [mode, setMode] = useState<'queue' | 'slots'>('queue');
  const [timezone, setTimezone] = useState('Africa/Cairo');
  const [slots, setSlots] = useState<Slot[]>([]);
  const [appointments, setAppointments] = useState<Appointment[]>([]);
  const [loading, setLoading] = useState(false);
  const [activeTab, setActiveTab] = useState('appointments');
  const [selectedBookingDay, setSelectedBookingDay] = useState(dayjs().format('YYYY-MM-DD'));
  const [bookingSearch, setBookingSearch] = useState('');
  const [bookingFilter, setBookingFilter] = useState<BookingFilter>('all');
  const [bookingStatusFilter, setBookingStatusFilter] = useState<BookingStatusFilter>('active');
  const [dateRange, setDateRange] = useState<[Dayjs, Dayjs]>([dayjs().add(1, 'day'), dayjs().add(30, 'day')]);
  const [selectedWeekdays, setSelectedWeekdays] = useState<number[]>([0, 1, 2, 3, 4, 5, 6]);
  const [startMinutes, setStartMinutes] = useState(14 * 60);
  const [lastStartMinutes, setLastStartMinutes] = useState(15 * 60);
  const [capacity, setCapacity] = useState(5);
  const [slotDuration, setSlotDuration] = useState(30);
  const [creatingSlots, setCreatingSlots] = useState(false);
  const [singleDate, setSingleDate] = useState<Dayjs>(dayjs().add(1, 'day'));
  const [singleStartMinutes, setSingleStartMinutes] = useState(14 * 60);
  const [singleCapacity, setSingleCapacity] = useState(5);
  const [singleDuration, setSingleDuration] = useState(30);
  const [creatingSingleSlot, setCreatingSingleSlot] = useState(false);
  const [savingMode, setSavingMode] = useState(false);
  const [editingSlot, setEditingSlot] = useState<Slot | null>(null);
  const [editDate, setEditDate] = useState<Dayjs>(dayjs());
  const [editStart, setEditStart] = useState<Dayjs>(dayjs());
  const [editEnd, setEditEnd] = useState<Dayjs>(dayjs());
  const [editCapacity, setEditCapacity] = useState(1);
  const [editEnabled, setEditEnabled] = useState(true);
  const [deleteAllOpen, setDeleteAllOpen] = useState(false);
  const [deleteAllConfirmation, setDeleteAllConfirmation] = useState('');
  const [deletingAll, setDeletingAll] = useState(false);
  const navigate = useNavigate();
  const { doctorId } = useDoctorContext();
  const { clinics, selectedClinicId, selectedClinic, setSelectedClinicId, loading: clinicsLoading } = useClinicContext();
  const effectiveDoctorId = doctorId || localStorage.getItem('doctorId') || '';
  const clinicId = selectedClinicId && selectedClinicId !== '__all_clinics__' ? selectedClinicId : localStorage.getItem('clinicId') || '';
  const loadRequestRef = useRef(0);

  useEffect(() => {
    if ((!selectedClinicId || selectedClinicId === '__all_clinics__') && clinics.length > 0) {
      setSelectedClinicId(clinics[0]._id);
    }
  }, [clinics, selectedClinicId, setSelectedClinicId]);

  const endpoint = useCallback((suffix: string) => `${API.BASE_URL}${BASE}/${encodeURIComponent(effectiveDoctorId)}${suffix}`, [effectiveDoctorId]);

  const load = useCallback(async () => {
    if (!effectiveDoctorId || !clinicId || clinicId === '__all_clinics__') return;
    const requestId = ++loadRequestRef.current;
    setLoading(true);
    try {
      const from = dayjs().startOf('day').toISOString();
      const to = dayjs().add(12, 'month').endOf('day').toISOString();
      const [modeRes, slotsRes, appointmentsRes] = await Promise.all([
        axios.get(endpoint('/mode'), { params: { clinic_id: clinicId } }),
        axios.get(endpoint('/slots'), { params: { clinic_id: clinicId, from, to } }),
        axios.get(endpoint('/appointments'), { params: { clinic_id: clinicId, from, to } }),
      ]);
      if (requestId !== loadRequestRef.current) return;
      setMode(modeRes.data.settings?.mode || 'queue');
      setTimezone(modeRes.data.settings?.timezone || 'Africa/Cairo');
      setSlots(slotsRes.data.slots || []);
      setAppointments(appointmentsRes.data.appointments || []);
    } catch (error: any) { message.error(error.response?.data?.message || text.loadError); }
    finally { if (requestId === loadRequestRef.current) setLoading(false); }
  }, [effectiveDoctorId, clinicId, endpoint, text.loadError]);

  useEffect(() => { load(); }, [load]);

  const saveMode = async (nextMode: 'queue' | 'slots') => {
    if (!clinicId || clinicId === '__all_clinics__') return;
    const targetClinicId = clinicId;
    try {
      setSavingMode(true);
      await axios.put(endpoint('/mode'), { clinic_id: targetClinicId, mode: nextMode, timezone });
      if (targetClinicId === clinicId) setMode(nextMode);
      message.success(`${text.modeSaved}${selectedClinic?.name ? ` (${selectedClinic.name})` : ''}`);
    } catch (error: any) { message.error(error.response?.data?.message || text.modeError); }
    finally { setSavingMode(false); }
  };

  const activateScheduledAppointments = async () => {
    await axios.put(endpoint('/mode'), { clinic_id: clinicId, mode: 'slots', timezone });
    setMode('slots');
  };

  const slotsToCreate = useMemo(() => {
    if (!dateRange[0] || !dateRange[1] || !selectedWeekdays.length || lastStartMinutes < startMinutes) return [];
    const generated: Array<{ start_at: string; end_at: string; capacity: number }> = [];
    let cursor = dateRange[0].startOf('day');
    const lastDay = dateRange[1].startOf('day');
    while (!cursor.isAfter(lastDay)) {
      if (selectedWeekdays.includes(cursor.day())) {
        for (let minutes = startMinutes; minutes <= lastStartMinutes; minutes += slotDuration) {
          const startAt = appointmentWallTimeToIso(cursor.format('YYYY-MM-DD'), minutes, timezone);
          const endAt = dayjs(startAt).add(slotDuration, 'minute').toISOString();
          if (dayjs(startAt).isAfter(dayjs())) generated.push({ start_at: startAt, end_at: endAt, capacity });
        }
      }
      cursor = cursor.add(1, 'day');
    }
    return generated;
  }, [dateRange, selectedWeekdays, startMinutes, lastStartMinutes, slotDuration, capacity, timezone]);

  const createFutureSlots = async () => {
    if (lastStartMinutes < startMinutes) return message.error(text.invalidEnd);
    if (!selectedWeekdays.length) return message.error(text.selectWorkday);
    if (!slotsToCreate.length) return message.error(text.noGenerated);
    if (slotsToCreate.length > 2000) return message.error(text.tooMany);
    setCreatingSlots(true);
    try {
      const wasQueueMode = mode !== 'slots';
      const response = await axios.post(endpoint('/slots/bulk'), { clinic_id: clinicId, slots: slotsToCreate });
      const created = response.data?.created || 0;
      const skipped = response.data?.skipped_existing || 0;
      if (wasQueueMode) {
        try { await activateScheduledAppointments(); }
        catch { message.error({ content: text.autoModeError, duration: 8 }); await load(); return; }
      }
      message.success(language === 'ar'
        ? `تم إنشاء ${created} موعد${skipped ? ` — وتم تجاهل ${skipped} موعد موجود مسبقاً` : ''}${wasQueueMode ? ` — ${text.autoModeActivated}` : ''}`
        : `Created ${created} slots${skipped ? ` — skipped ${skipped} existing slots` : ''}${wasQueueMode ? ` — ${text.autoModeActivated}` : ''}`);
      await load();
    } catch (error: any) { message.error(error.response?.data?.message || text.createError); }
    finally { setCreatingSlots(false); }
  };

  const createSingleSlot = async () => {
    const startAt = appointmentWallTimeToIso(singleDate.format('YYYY-MM-DD'), singleStartMinutes, timezone);
    if (!dayjs(startAt).isAfter(dayjs())) return message.error(text.futureOnly);
    setCreatingSingleSlot(true);
    try {
      const wasQueueMode = mode !== 'slots';
      await axios.post(endpoint('/slots'), {
        clinic_id: clinicId,
        start_at: startAt,
        end_at: dayjs(startAt).add(singleDuration, 'minute').toISOString(),
        capacity: singleCapacity,
      });
      if (wasQueueMode) {
        try { await activateScheduledAppointments(); }
        catch { message.error({ content: text.autoModeError, duration: 8 }); await load(); return; }
      }
      message.success(language === 'ar'
        ? `تم إنشاء موعد ${formatAppointmentDateTime(startAt, language, timezone)}${wasQueueMode ? ` — ${text.autoModeActivated}` : ''}`
        : `Slot created: ${formatAppointmentDateTime(startAt, language, timezone)}${wasQueueMode ? ` — ${text.autoModeActivated}` : ''}`);
      await load();
    } catch (error: any) { message.error(error.response?.data?.message || text.createOneError); }
    finally { setCreatingSingleSlot(false); }
  };

  const updateSlot = async (slot: Slot, patch: object) => {
    try { await axios.put(endpoint(`/slots/${slot._id}`), patch); await load(); }
    catch (error: any) { message.error(error.response?.data?.message || text.updateError); }
  };

  const beginEditSlot = (slot: Slot) => {
    const localStart = appointmentInstantToWallTime(slot.start_at, timezone);
    const localEnd = appointmentInstantToWallTime(slot.end_at, timezone);
    setEditingSlot(slot);
    setEditDate(dayjs(localStart.dateKey));
    setEditStart(dayjs().startOf('day').add(localStart.minutes, 'minute'));
    setEditEnd(dayjs().startOf('day').add(localEnd.minutes, 'minute'));
    setEditCapacity(slot.capacity);
    setEditEnabled(slot.enabled);
  };

  const saveSlotChanges = async () => {
    if (!editingSlot) return;
    const dateKey = editDate.format('YYYY-MM-DD');
    const startAt = appointmentWallTimeToIso(dateKey, editStart.hour() * 60 + editStart.minute(), timezone);
    const endAt = appointmentWallTimeToIso(dateKey, editEnd.hour() * 60 + editEnd.minute(), timezone);
    if (editCapacity < editingSlot.reserved_count) return message.error(`${text.capacityError} (${editingSlot.reserved_count})`);
    if (!editingSlot.reserved_count && !dayjs(endAt).isAfter(dayjs(startAt))) return message.error(text.endAfterStart);
    const patch: Record<string, any> = { capacity: editCapacity, enabled: editEnabled };
    if (!editingSlot.reserved_count) {
      patch.start_at = startAt;
      patch.end_at = endAt;
    }
    try {
      await axios.put(endpoint(`/slots/${editingSlot._id}`), patch);
      message.success(text.updated);
      setEditingSlot(null);
      await load();
    } catch (error: any) { message.error(error.response?.data?.message || text.updateError); }
  };

  const visibleAppointments = useMemo(() => appointments.filter(appointment => {
    const stillUpcoming = dayjs(appointment.end_at || appointment.start_at).isAfter(dayjs());
    if (!stillUpcoming) return false;
    const isActive = ACTIVE_APPOINTMENT_STATUSES.has(appointment.status);
    if (bookingStatusFilter === 'active') return isActive;
    if (bookingStatusFilter === 'inactive') return !isActive;
    return true;
  }), [appointments, bookingStatusFilter]);

  const scheduleEntries = useMemo<ScheduleEntry[]>(() => {
    const appointmentsBySlot = new Map<string, Appointment[]>();
    visibleAppointments.forEach(appointment => {
      const key = appointment.slot_id || `time:${appointment.start_at}`;
      appointmentsBySlot.set(key, [...(appointmentsBySlot.get(key) || []), appointment]);
    });

    const entries: ScheduleEntry[] = slots.filter(slot => dayjs(slot.end_at).isAfter(dayjs())).map(slot => ({
      key: slot._id,
      slot,
      start_at: slot.start_at,
      end_at: slot.end_at,
      appointments: appointmentsBySlot.get(slot._id) || [],
      archived: false,
    }));
    const liveSlotIds = new Set(slots.map(slot => slot._id));
    appointmentsBySlot.forEach((rows, key) => {
      if (!liveSlotIds.has(key)) {
        entries.push({ key: `archived:${key}`, start_at: rows[0].start_at, end_at: rows[0].end_at, appointments: rows, archived: true });
      }
    });
    return entries.sort((a, b) => new Date(a.start_at).getTime() - new Date(b.start_at).getTime());
  }, [slots, visibleAppointments]);

  const bookingDays = useMemo(() => {
    const groups = new Map<string, { slots: number; patients: number }>();
    scheduleEntries.forEach(entry => {
      const key = dayjs(entry.start_at).format('YYYY-MM-DD');
      const current = groups.get(key) || { slots: 0, patients: 0 };
      groups.set(key, { slots: current.slots + 1, patients: current.patients + entry.appointments.length });
    });
    return Array.from(groups.entries()).map(([date, counts]) => ({ date, ...counts }));
  }, [scheduleEntries]);

  useEffect(() => {
    if (!bookingDays.length) return;
    if (!bookingDays.some(day => day.date === selectedBookingDay)) {
      const today = dayjs().format('YYYY-MM-DD');
      setSelectedBookingDay(bookingDays.find(day => day.date >= today)?.date || bookingDays[0].date);
    }
  }, [bookingDays, selectedBookingDay]);

  const selectedScheduleEntries = useMemo(() => {
    const normalizedSearch = bookingSearch.trim().toLocaleLowerCase();
    return scheduleEntries.filter(entry => {
      if (dayjs(entry.start_at).format('YYYY-MM-DD') !== selectedBookingDay) return false;
      const capacity = entry.slot?.capacity || entry.appointments.length;
      const reserved = entry.slot?.reserved_count ?? entry.appointments.filter(row => ACTIVE_APPOINTMENT_STATUSES.has(row.status)).length;
      const available = Math.max(0, capacity - reserved);
      if (bookingFilter === 'booked' && !entry.appointments.length) return false;
      if (bookingFilter === 'available' && (!entry.slot?.enabled || available < 1)) return false;
      if (!normalizedSearch) return true;
      return entry.appointments.some(row => `${row.patient_name} ${row.patient_phone}`.toLocaleLowerCase().includes(normalizedSearch));
    });
  }, [scheduleEntries, selectedBookingDay, bookingSearch, bookingFilter]);

  const bookingSummary = useMemo(() => {
    const activeAppointments = appointments.filter(row => ACTIVE_APPOINTMENT_STATUSES.has(row.status) && dayjs(row.end_at || row.start_at).isAfter(dayjs()));
    const today = dayjs().format('YYYY-MM-DD');
    const next = activeAppointments.find(row => dayjs(row.start_at).isAfter(dayjs()));
    return {
      today: activeAppointments.filter(row => dayjs(row.start_at).format('YYYY-MM-DD') === today).length,
      patients: activeAppointments.length,
      openSeats: slots.filter(slot => slot.enabled && dayjs(slot.end_at).isAfter(dayjs())).reduce((sum, slot) => sum + Math.max(0, slot.available), 0),
      next,
    };
  }, [appointments, slots]);

  const repeatDays = useMemo(() => Math.max(1, dateRange[1].startOf('day').diff(dateRange[0].startOf('day'), 'day') + 1), [dateRange]);
  const publishedSlotGroups = useMemo(() => {
    const groups = new Map<string, Slot[]>();
    slots.forEach(slot => {
      const key = dayjs(slot.start_at).format('YYYY-MM-DD');
      groups.set(key, [...(groups.get(key) || []), slot]);
    });
    return Array.from(groups.entries());
  }, [slots]);

  const deleteSlot = async (slot: Slot) => {
    try {
      await axios.delete(endpoint(`/slots/${slot._id}`));
      message.success(text.deleted);
      await load();
    } catch (error: any) { message.error(error.response?.data?.message || text.deleteError); }
  };

  const branchConfirmationText = selectedClinic?.name || clinicId;
  const deleteAllSlots = async () => {
    if (!slots.length || deleteAllConfirmation.trim() !== branchConfirmationText.trim()) return;
    setDeletingAll(true);
    try {
      const response = await axios.delete(endpoint('/slots'), {
        data: {
          clinic_id: clinicId,
          slot_ids: slots.map(slot => slot._id),
          expected_count: slots.length,
          confirmation: 'DELETE_ALL_UPCOMING_SLOTS',
        },
      });
      message.success(`${text.deleteAllSuccess} (${response.data?.deleted || slots.length})`);
      setDeleteAllOpen(false);
      setDeleteAllConfirmation('');
      await load();
    } catch (error: any) {
      message.error(error.response?.data?.message || text.deleteError);
    } finally {
      setDeletingAll(false);
    }
  };

  const slotColumns = [
    { title: text.date, dataIndex: 'start_at', render: (v: string) => dayjs(v).locale(locale).format('DD/MM/YYYY') },
    { title: text.time, dataIndex: 'start_at', render: (v: string) => formatAppointmentInstant(v, language, timezone) },
    { title: text.capacity, dataIndex: 'capacity', render: (v: number, row: Slot) => <InputNumber min={row.reserved_count || 1} value={v} onChange={(n) => n && updateSlot(row, { capacity: n })} /> },
    { title: text.reserved, dataIndex: 'reserved_count' },
    { title: text.available, render: (_: any, row: Slot) => row.is_full ? <Tag color="red">{text.full}</Tag> : <Tag color="green">{row.available}</Tag> },
    { title: text.enabled, render: (_: any, row: Slot) => <Switch checked={row.enabled} onChange={(enabled) => updateSlot(row, { enabled })} /> },
    { title: text.actions, render: (_: any, row: Slot) => <Space><Button icon={<EditOutlined />} onClick={() => beginEditSlot(row)}>{text.edit}</Button><Popconfirm title={text.deleteKeep} onConfirm={() => deleteSlot(row)}><Button danger icon={<DeleteOutlined />}>{text.delete}</Button></Popconfirm></Space> },
  ];

  const openPatientFile = (appointment: Appointment) => {
    navigate(`/dashboard?${appointment.patient_id ? `scheduled_patient_id=${encodeURIComponent(appointment.patient_id)}` : `scheduled_patient_phone=${encodeURIComponent(appointment.patient_phone)}`}`);
  };

  const bookingsOverview = <div className="booking-overview">
    <section className="booking-hero">
      <div>
        <span className="booking-eyebrow"><CheckCircleOutlined /> {bookingCopy[language].readOnly}</span>
        <h2>{bookingCopy[language].overview}</h2>
        <p>{bookingCopy[language].overviewHint}</p>
      </div>
      <Button type="primary" ghost icon={<ReloadOutlined />} onClick={load} loading={loading}>{text.refresh}</Button>
    </section>

    <div className="booking-stat-grid">
      <article className="booking-stat-card is-today"><span className="booking-stat-icon"><CalendarOutlined /></span><div><small>{bookingCopy[language].today}</small><strong>{bookingSummary.today}</strong></div></article>
      <article className="booking-stat-card is-patients"><span className="booking-stat-icon"><TeamOutlined /></span><div><small>{bookingCopy[language].upcomingPatients}</small><strong>{bookingSummary.patients}</strong></div></article>
      <article className="booking-stat-card is-open"><span className="booking-stat-icon"><AppstoreOutlined /></span><div><small>{bookingCopy[language].openSeats}</small><strong>{bookingSummary.openSeats}</strong></div></article>
      <article className="booking-stat-card is-next"><span className="booking-stat-icon"><ClockCircleOutlined /></span><div><small>{bookingCopy[language].nextBooking}</small><strong>{bookingSummary.next ? formatAppointmentDateTime(bookingSummary.next.start_at, language, timezone) : bookingCopy[language].noNextBooking}</strong></div></article>
    </div>

    <Card className="booking-workspace" bordered={false}>
      <div className="booking-toolbar">
        <div className="booking-toolbar-title"><CalendarOutlined /><div><strong>{bookingCopy[language].chooseDay}</strong><span>{bookingDays.length} {bookingCopy[language].slots}</span></div></div>
        <div className="booking-filters">
          <Input allowClear prefix={<SearchOutlined />} value={bookingSearch} onChange={event => setBookingSearch(event.target.value)} placeholder={bookingCopy[language].search} />
          <Select<BookingFilter> value={bookingFilter} onChange={setBookingFilter} options={[
            { value: 'all', label: bookingCopy[language].allSlots },
            { value: 'booked', label: bookingCopy[language].bookedOnly },
            { value: 'available', label: bookingCopy[language].availableOnly },
          ]} />
          <Select<BookingStatusFilter> value={bookingStatusFilter} onChange={setBookingStatusFilter} options={[
            { value: 'active', label: bookingCopy[language].activeBookings },
            { value: 'all', label: bookingCopy[language].allBookings },
            { value: 'inactive', label: bookingCopy[language].cancelledBookings },
          ]} />
        </div>
      </div>

      <div className="booking-day-strip" role="tablist" aria-label={bookingCopy[language].chooseDay}>
        {bookingDays.map(day => {
          const value = dayjs(day.date).locale(locale);
          return <button type="button" role="tab" aria-selected={selectedBookingDay === day.date} key={day.date} className={`booking-day-chip${selectedBookingDay === day.date ? ' is-selected' : ''}`} onClick={() => setSelectedBookingDay(day.date)}>
            <span>{value.format('dddd')}</span><strong>{value.format('DD')}</strong><small>{value.format('MMM')} · {day.patients} {bookingCopy[language].patients}</small>
          </button>;
        })}
      </div>

      <div className="booking-day-heading">
        <div><span>{dayjs(selectedBookingDay).locale(locale).format('dddd')}</span><h3>{dayjs(selectedBookingDay).locale(locale).format('DD MMMM YYYY')}</h3></div>
        <Tag color="blue">{selectedScheduleEntries.length} {bookingCopy[language].slots}</Tag>
      </div>

      <div className="booking-slot-list">
        {selectedScheduleEntries.map(entry => {
          const activePatients = entry.appointments.filter(row => ACTIVE_APPOINTMENT_STATUSES.has(row.status));
          const capacity = entry.slot?.capacity || Math.max(1, activePatients.length);
          const reserved = entry.slot?.reserved_count ?? activePatients.length;
          const available = Math.max(0, capacity - reserved);
          const isClosed = entry.archived || entry.slot?.enabled === false;
          const isFull = !isClosed && available === 0;
          const occupancy = Math.min(100, Math.round((reserved / Math.max(1, capacity)) * 100));
          return <article key={entry.key} className={`booking-slot-card${isFull ? ' is-full' : ''}${isClosed ? ' is-closed' : ''}`}>
            <aside className="booking-slot-time">
              <ClockCircleOutlined />
              <strong dir="ltr">{formatAppointmentInstant(entry.start_at, language, timezone)}</strong>
              {entry.end_at && <small dir="ltr">{formatAppointmentInstant(entry.end_at, language, timezone)}</small>}
            </aside>
            <div className="booking-slot-content">
              <div className="booking-slot-head">
                <div>
                  <Tag color={isClosed ? 'default' : isFull ? 'red' : 'green'}>{isClosed ? bookingCopy[language].closed : isFull ? bookingCopy[language].full : `${available} ${bookingCopy[language].remaining}`}</Tag>
                  <strong>{bookingCopy[language].bookedPatients}: {entry.appointments.length}</strong>
                </div>
                {!entry.archived && <div className="booking-capacity"><span>{reserved}/{capacity} {bookingCopy[language].seats}</span><Progress percent={occupancy} showInfo={false} size="small" strokeColor={isFull ? '#ef4444' : '#22c55e'} /></div>}
              </div>
              {entry.archived && <Alert className="booking-archived-alert" type="warning" showIcon message={bookingCopy[language].archivedSlot} description={bookingCopy[language].archivedHelp} />}
              <div className="booking-patient-list">
                {entry.appointments.map(appointment => <div key={appointment._id} className="booking-patient-row">
                  <Avatar className="booking-patient-avatar">{patientInitials(appointment.patient_name)}</Avatar>
                  <div className="booking-patient-info"><strong>{appointment.patient_name}</strong><span dir="ltr"><PhoneOutlined /> {appointment.patient_phone || '—'}</span></div>
                  <Tag color={appointmentStatusColor(appointment.status)}>{text.statuses[appointment.status as keyof typeof text.statuses] || appointment.status}</Tag>
                  <div className="booking-patient-actions">
                    {appointment.patient_phone && <Button type="text" icon={<PhoneOutlined />} href={`tel:${appointment.patient_phone}`} aria-label={bookingCopy[language].call} />}
                    <Button type="primary" ghost onClick={() => openPatientFile(appointment)}>{bookingCopy[language].openFile} <RightOutlined /></Button>
                  </div>
                </div>)}
                {!entry.appointments.length && <div className="booking-empty-slot"><UserOutlined /><span>{bookingCopy[language].noPatients}</span></div>}
              </div>
            </div>
          </article>;
        })}
        {!selectedScheduleEntries.length && <Empty className="booking-empty-day" description={bookingCopy[language].noDaySlots} />}
      </div>
    </Card>
  </div>;

  return <Layout style={{ minHeight: '100vh' }} dir={isRTL ? 'rtl' : 'ltr'}>
    <DashboardSidebar collapsed={collapsed} setCollapsed={setCollapsed} />
    <Layout>
      <DashboardHeader onSettingsClick={() => navigate('/settings')} onMenuClick={() => setCollapsed(!collapsed)} isMobile={false} />
      <Content className="timetable-page" style={{ padding: 24, background: '#f5f8fc' }}>
        <Space style={{ width: '100%', justifyContent: 'space-between', marginBottom: 18 }} wrap>
          <Space><Button icon={<ArrowLeftOutlined />} onClick={() => navigate('/dashboard')} /><div><Title level={2} style={{ margin: 0 }}>{text.title}</Title><Text type="secondary">{text.subtitle}</Text></div></Space>
          <Button icon={<ReloadOutlined />} onClick={load} loading={loading}>{text.refresh}</Button>
        </Space>
        <Card className="timetable-branch-controls" style={{ marginBottom: 16 }}>
          <Space wrap size="middle">
            <Text strong>{text.branch}</Text>
            <Select
              showSearch
              optionFilterProp="label"
              value={clinicId && clinicId !== '__all_clinics__' ? clinicId : undefined}
              placeholder={text.selectBranch}
              loading={clinicsLoading}
              disabled={savingMode}
              style={{ minWidth: 310 }}
              onChange={setSelectedClinicId}
              options={clinics.map(clinic => ({ value: clinic._id, label: clinic.name }))}
            />
            {(loading || clinicsLoading) && <Spin size="small" />}
            <Text strong>{text.workMode}</Text>
            <Select
              value={mode}
              loading={loading || savingMode}
              disabled={loading || savingMode || !clinicId}
              style={{ width: 180 }}
              onChange={saveMode}
              options={[{ value: 'queue', label: text.queue }, { value: 'slots', label: text.slotsMode }]}
            />
            <Text>{text.timezone}</Text>
            <Select
              showSearch
              value={timezone}
              disabled={loading || savingMode || !clinicId}
              style={{ width: 190 }}
              onChange={setTimezone}
              onBlur={() => { if (!loading && !savingMode) saveMode(mode); }}
              options={['Africa/Cairo','Asia/Riyadh','Asia/Dubai','Europe/London'].map(value => ({ value, label: value }))}
            />
          </Space>
          <Text type="secondary" style={{ display: 'block', marginTop: 10 }}>
            {text.modeHelp}
          </Text>
        </Card>
        <Spin spinning={loading} tip={text.loadingBranch}>
        <Tabs activeKey={activeTab} onChange={setActiveTab} items={[
          { key: 'appointments', label: `${bookingCopy[language].overview} (${bookingSummary.patients})`, children: bookingsOverview },
          { key: 'slots', label: bookingCopy[language].manage, children: <>
            <div className="timetable-builder-grid">
              <Card
                className="slot-generator-card"
                title={<h3 className="slot-generator-title"><ThunderboltOutlined /> {text.generator}</h3>}
              >
                <div className="slot-generator-fields">
                  <div>
                    <span className="slot-field-label">{text.startDate}</span>
                    <DatePicker
                      value={dateRange[0]}
                      disabledDate={current => current && current.isBefore(dayjs().startOf('day'))}
                      onChange={value => value && setDateRange([value, value.add(repeatDays - 1, 'day')])}
                      style={{ width: '100%' }}
                    />
                  </div>
                  <div>
                    <span className="slot-field-label">{text.repeatDays}</span>
                    <InputNumber
                      min={1}
                      max={365}
                      value={repeatDays}
                      onChange={value => setDateRange([dateRange[0], dateRange[0].add((value || 1) - 1, 'day')])}
                      style={{ width: '100%' }}
                    />
                  </div>
                  <div>
                    <span className="slot-field-label">{text.dayStart}</span>
                    <Select value={startMinutes} onChange={minutes => { setStartMinutes(minutes); if (lastStartMinutes < minutes) setLastStartMinutes(minutes); }} options={timeOptions} style={{ width: '100%' }} suffixIcon={<ClockCircleOutlined />} />
                  </div>
                  <div>
                    <span className="slot-field-label">{text.dayEnd}</span>
                    <Select value={lastStartMinutes} onChange={setLastStartMinutes} options={timeOptions.filter(option => option.value >= startMinutes)} style={{ width: '100%' }} suffixIcon={<ClockCircleOutlined />} />
                  </div>
                  <div>
                    <span className="slot-field-label">{text.duration}</span>
                    <InputNumber min={5} max={240} step={5} value={slotDuration} onChange={value => setSlotDuration(value || 5)} style={{ width: '100%' }} />
                  </div>
                  <div>
                    <span className="slot-field-label">{text.capacity}</span>
                    <InputNumber min={1} max={10000} value={capacity} onChange={value => setCapacity(value || 1)} style={{ width: '100%' }} />
                  </div>
                </div>

                <div className="slot-working-days">
                  <div className="slot-working-days-header">
                    <span className="slot-field-label" style={{ margin: 0 }}>{text.workDays}</span>
                    <div className="slot-presets">
                      <button type="button" className="slot-preset-button" onClick={() => setSelectedWeekdays([0, 1, 2, 3, 4])}>{text.sunThu}</button>
                      <button type="button" className="slot-preset-button" onClick={() => setSelectedWeekdays([5, 6])}>{text.weekend}</button>
                      <button type="button" className="slot-preset-button" onClick={() => setSelectedWeekdays([0, 1, 2, 3, 4, 5, 6])}>{text.all}</button>
                    </div>
                  </div>
                  <div className="slot-day-buttons">
                    {WEEKDAY_VALUES.map(day => (
                      <button
                        type="button"
                        key={day}
                        className={`slot-day-button${selectedWeekdays.includes(day) ? ' is-selected' : ''}`}
                        onClick={() => setSelectedWeekdays(current => current.includes(day) ? current.filter(value => value !== day) : [...current, day])}
                      >
                        {text.weekdays[day]}
                      </button>
                    ))}
                  </div>
                </div>

                <div className="slot-generation-preview">
                  <strong>{slotsToCreate.length ? `${slotsToCreate.length} ${text.appointmentsWord} ${text.during} ${repeatDays} ${text.days}` : text.noGenerated}</strong>
                  {slotsToCreate.length > 0 && (
                    <span>
                      {text.first}: {formatAppointmentDateTime(slotsToCreate[0].start_at, language, timezone)} — {text.last}: {formatAppointmentDateTime(slotsToCreate[slotsToCreate.length - 1].start_at, language, timezone)}
                    </span>
                  )}
                </div>
                <Button className="publish-slots-button" type="primary" size="large" block icon={<CalendarOutlined />} loading={creatingSlots} onClick={createFutureSlots} disabled={!slotsToCreate.length}>
                  {text.publish} {slotsToCreate.length || 0} {text.appointment}
                </Button>

                <div className="single-slot-panel">
                  <div className="single-slot-heading"><PlusOutlined /> {text.addSingle}</div>
                  <div className="single-slot-fields">
                    <div><span className="slot-field-label">{text.date}</span><DatePicker value={singleDate} disabledDate={current => current && current.isBefore(dayjs().startOf('day'))} onChange={value => value && setSingleDate(value)} style={{ width: '100%' }} /></div>
                    <div><span className="slot-field-label">{text.time}</span><Select value={singleStartMinutes} onChange={setSingleStartMinutes} options={timeOptions} style={{ width: '100%' }} /></div>
                    <div><span className="slot-field-label">{text.duration}</span><InputNumber min={5} max={240} step={5} value={singleDuration} onChange={value => setSingleDuration(value || 5)} style={{ width: '100%' }} /></div>
                    <div><span className="slot-field-label">{text.capacity}</span><InputNumber min={1} max={10000} value={singleCapacity} onChange={value => setSingleCapacity(value || 1)} style={{ width: '100%' }} /></div>
                    <Button type="primary" icon={<PlusOutlined />} loading={creatingSingleSlot} onClick={createSingleSlot}>{text.add}</Button>
                  </div>
                </div>
              </Card>

              <Card
                className="published-slots-card"
                title={<h3 className="published-slots-title"><CalendarOutlined /> {text.published}</h3>}
                extra={<Button type="text" icon={<ReloadOutlined />} onClick={load} loading={loading} />}
              >
                <div className="published-slots-summary">
                  <Tag color="blue">{text.total} {slots.length}</Tag>
                  <Tag color="green">{text.available} {slots.filter(slot => slot.enabled && !slot.is_full).length}</Tag>
                  <Tag color="red">{text.full} {slots.filter(slot => slot.is_full).length}</Tag>
                </div>
                <Button
                  className="delete-all-slots-button"
                  danger
                  block
                  icon={<DeleteOutlined />}
                  disabled={!slots.length}
                  onClick={() => { setDeleteAllConfirmation(''); setDeleteAllOpen(true); }}
                >
                  {text.deleteAll}
                </Button>
                <div className="published-slots-scroll">
                  {publishedSlotGroups.map(([day, daySlots]) => (
                    <section className="published-day" key={day}>
                      <div className="published-day-header">
                        <strong>{dayjs(day).locale(locale).format('dddd، DD/MM/YYYY')}</strong>
                        <small>{daySlots.filter(slot => slot.enabled && !slot.is_full).length} {text.available}</small>
                      </div>
                      <div className="published-time-list">
                        {daySlots.map(slot => (
                          <div key={slot._id} className={`published-slot-pill${slot.reserved_count > 0 ? ' is-booked' : ''}${slot.is_full ? ' is-full' : ''}${!slot.enabled ? ' is-disabled' : ''}`}>
                            <button
                              type="button"
                              className="published-slot-main"
                              onClick={() => beginEditSlot(slot)}
                              title={slot.reserved_count > 0 ? text.editBookedHint : text.edit}
                            >
                              <ClockCircleOutlined className="published-slot-clock" />
                              <span dir="ltr">{formatAppointmentInstant(slot.start_at, language, timezone)}</span>
                              <small className="published-slot-availability">
                                {!slot.enabled ? text.closed : slot.is_full ? text.full : `${slot.available} ${text.available}`}
                              </small>
                            </button>
                            {slot.reserved_count === 0 && (
                              <Popconfirm title={text.deleteQuestion} onConfirm={() => deleteSlot(slot)}>
                                <button type="button" className="published-slot-delete" aria-label={text.delete}><DeleteOutlined /></button>
                              </Popconfirm>
                            )}
                          </div>
                        ))}
                      </div>
                    </section>
                  ))}
                  {!publishedSlotGroups.length && <Alert type="info" showIcon message={text.noPublished} />}
                </div>
              </Card>
            </div>
            <Card
              className="slot-details-card"
              title={<h3 className="slot-details-title"><ClockCircleOutlined /> {text.details}</h3>}
            >
              <Table rowKey="_id" loading={loading} dataSource={slots} columns={slotColumns} pagination={{ pageSize: 20, showSizeChanger: true }} scroll={{ x: 900 }} />
            </Card>
          </> },
        ]} />
        </Spin>
        <Modal title={text.edit} open={!!editingSlot} onCancel={() => setEditingSlot(null)} onOk={saveSlotChanges} okText={text.save} cancelText={text.cancel}>
          <Space direction="vertical" size="middle" style={{ width: '100%' }}>
            <div>
              <Text strong style={{ display: 'block', marginBottom: 7 }}>{text.dateTime}</Text>
              <Space wrap>
                <DatePicker value={editDate} disabled={!!editingSlot?.reserved_count} onChange={v => v && setEditDate(v)} />
                <Select disabled={!!editingSlot?.reserved_count} value={editStart.hour() * 60 + editStart.minute()} options={timeOptions} onChange={minutes => setEditStart(dayjs().startOf('day').add(minutes, 'minute'))} style={{ width: 130 }} />
                <Select disabled={!!editingSlot?.reserved_count} value={editEnd.hour() * 60 + editEnd.minute()} options={timeOptions} onChange={minutes => setEditEnd(dayjs().startOf('day').add(minutes, 'minute'))} style={{ width: 130 }} />
              </Space>
            </div>
            <div>
              <Text strong style={{ display: 'block', marginBottom: 7 }}>{text.capacity}</Text>
              <InputNumber
                min={Math.max(1, editingSlot?.reserved_count || 0)}
                max={10000}
                value={editCapacity}
                onChange={value => setEditCapacity(value || Math.max(1, editingSlot?.reserved_count || 1))}
                style={{ width: '100%' }}
                addonAfter={`${text.currentReserved}: ${editingSlot?.reserved_count || 0}`}
              />
            </div>
            <Space>
              <Text strong>{text.activeSlot}</Text>
              <Switch checked={editEnabled} onChange={setEditEnabled} />
            </Space>
            {editingSlot?.reserved_count ? <Alert type="info" showIcon message={text.lockedHelp} /> : null}
          </Space>
        </Modal>
        <Modal
          title={text.deleteAllTitle}
          open={deleteAllOpen}
          okText={`${text.delete} ${slots.length}`}
          cancelText={text.cancel}
          okButtonProps={{ danger: true, disabled: deleteAllConfirmation.trim() !== branchConfirmationText.trim() }}
          confirmLoading={deletingAll}
          closable={!deletingAll}
          maskClosable={!deletingAll}
          onCancel={() => { if (!deletingAll) { setDeleteAllOpen(false); setDeleteAllConfirmation(''); } }}
          onOk={deleteAllSlots}
        >
          <Space direction="vertical" size="middle" style={{ width: '100%' }}>
            <Alert
              type="error"
              showIcon
              message={`${text.deleteAllTitle} (${slots.length})`}
              description={text.deleteAllWarning}
            />
            <div>
              <Text>{text.deleteAllType}</Text>
              <Text code copyable style={{ display: 'block', width: 'fit-content', marginTop: 8 }}>{branchConfirmationText}</Text>
            </div>
            <Input
              value={deleteAllConfirmation}
              disabled={deletingAll}
              status={deleteAllConfirmation && deleteAllConfirmation.trim() !== branchConfirmationText.trim() ? 'error' : undefined}
              placeholder={branchConfirmationText}
              onChange={event => setDeleteAllConfirmation(event.target.value)}
              onPressEnter={() => { if (deleteAllConfirmation.trim() === branchConfirmationText.trim()) deleteAllSlots(); }}
            />
            {deleteAllConfirmation && deleteAllConfirmation.trim() !== branchConfirmationText.trim() ? <Text type="danger">{text.deleteAllMismatch}</Text> : null}
          </Space>
        </Modal>
      </Content>
    </Layout>
  </Layout>;
}
